/**
 * Sprint 24+: Excel Upload Modal Component
 * 
 * Provides scoped upload functionality with:
 * - Scope chip display ("Uploading into: Portfolio > Program > Project")
 * - Local multi-file upload
 * - SharePoint/OneDrive link upload
 * - Dry-run preview mode
 * - Progress tracking
 * - Error CSV download
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Upload, FileSpreadsheet, X, Check, AlertTriangle,
  Cloud, Link2, Download, RefreshCw, ChevronDown,
  AlertCircle, FileWarning, Layers, Info
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { api } from '../api/client';
import { toast } from 'sonner';

const ExcelUploadModal = ({ 
  isOpen, 
  onClose, 
  scope = {}, // { portfolio_id, program_id, project_id, portfolio_name, program_name, project_name }
  onUploadComplete,
  title = "Upload Excel Data",
  scopeLocked = false  // When true, scope is locked and cannot be changed
}) => {
  const [uploadMode, setUploadMode] = useState('local'); // 'local' | 'sharepoint'
  const [files, setFiles] = useState([]);
  const [sharepointUrl, setSharepointUrl] = useState('');
  const [isDryRun, setIsDryRun] = useState(true);
  const [isUpsert, setIsUpsert] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [intakeStatus, setIntakeStatus] = useState(null);
  const [scopeDisplay, setScopeDisplay] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [versionWarning, setVersionWarning] = useState(null);
  
  // Determine if scope is actually locked (from contextual upload)
  const isContextualUpload = scopeLocked || Boolean(scope.project_id || scope.program_id);

  // Load intake status on mount
  useEffect(() => {
    if (isOpen) {
      loadIntakeStatus();
      resolveScopeDisplay();
    }
  }, [isOpen, scope]);

  const loadIntakeStatus = async () => {
    try {
      const response = await api.get('/intake/excel/v3/status');
      setIntakeStatus(response.data);
    } catch (error) {
      console.error('Failed to load intake status:', error);
    }
  };

  const resolveScopeDisplay = async () => {
    try {
      const params = new URLSearchParams();
      if (scope.portfolio_id) params.append('portfolio_id', scope.portfolio_id);
      if (scope.program_id) params.append('program_id', scope.program_id);
      if (scope.project_id) params.append('project_id', scope.project_id);
      
      const response = await api.get(`/intake/excel/v3/scope/resolve?${params.toString()}`);
      setScopeDisplay(response.data.display);
    } catch (error) {
      // Build display from props
      const parts = [];
      if (scope.portfolio_name) parts.push(scope.portfolio_name);
      if (scope.program_name) parts.push(scope.program_name);
      if (scope.project_name) parts.push(scope.project_name);
      setScopeDisplay(parts.join(' › ') || 'Global');
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files) {
      const newFiles = Array.from(e.dataTransfer.files).filter(
        f => f.name.endsWith('.xlsx') || f.name.endsWith('.xls') || f.name.endsWith('.csv')
      );
      setFiles(prev => [...prev, ...newFiles]);
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...newFiles]);
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    setUploading(true);
    setUploadProgress(0);
    setResults(null);
    setVersionWarning(null);

    try {
      if (uploadMode === 'local') {
        await handleLocalUpload();
      } else {
        await handleSharePointUpload();
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleLocalUpload = async () => {
    const params = new URLSearchParams();
    if (scope.portfolio_id) params.append('portfolio_id', scope.portfolio_id);
    if (scope.program_id) params.append('program_id', scope.program_id);
    if (scope.project_id) params.append('project_id', scope.project_id);
    params.append('mode', isUpsert ? 'upsert' : 'insert');
    params.append('dry_run', isDryRun.toString());
    params.append('scope_locked', isContextualUpload.toString());

    if (files.length === 1) {
      // Single file upload
      const formData = new FormData();
      formData.append('file', files[0]);
      
      const response = await api.post(`/intake/excel/v3?${params.toString()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      setResults(response.data);
      
      // Check for version warning
      if (response.data.version_warning) {
        setVersionWarning(response.data.version_warning);
      }
      
      if (response.data.success && !isDryRun) {
        toast.success(`Imported ${response.data.processed} records`);
        onUploadComplete?.(response.data);
      }
    } else {
      // Batch upload
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      
      const response = await api.post(`/intake/excel/v3/batch?${params.toString()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      setResults(response.data);
      
      if (response.data.success && !isDryRun) {
        toast.success(`Imported ${response.data.total_processed} records from ${files.length} files`);
        onUploadComplete?.(response.data);
      }
    }
  };

  const handleSharePointUpload = async () => {
    const response = await api.post('/intake/excel/v3/link', {
      source: sharepointUrl.includes('sharepoint') ? 'sharepoint' : 'onedrive',
      url: sharepointUrl,
      scope: {
        portfolio_id: scope.portfolio_id,
        program_id: scope.program_id,
        project_id: scope.project_id,
      },
      mode: isUpsert ? 'upsert' : 'insert',
      dry_run: isDryRun
    });
    
    setResults(response.data);
    setUploadProgress(100);
    
    if (response.data.success && !isDryRun) {
      toast.success(`Imported ${response.data.processed} records from SharePoint`);
      onUploadComplete?.(response.data);
    }
  };

  const handleDownloadErrors = async (jobId) => {
    try {
      const response = await api.get(`/intake/excel/v3/errors/${jobId}`);
      
      // Create and download CSV
      const blob = new Blob([response.data.csv_content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `errors_${jobId}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download error CSV');
    }
  };

  const handleProceed = async () => {
    if (isDryRun && results?.success) {
      // Switch to actual import
      setIsDryRun(false);
      setResults(null);
      handleUpload();
    }
  };

  const handleReset = () => {
    setFiles([]);
    setSharepointUrl('');
    setResults(null);
    setUploadProgress(0);
    setIsDryRun(true);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-blue-600" />
            {title}
          </DialogTitle>
        </DialogHeader>

        {/* Scope Chip - with locked state */}
        <div 
          className={`rounded-lg p-3 flex items-center gap-2 ${
            isContextualUpload 
              ? 'bg-purple-50 border border-purple-200' 
              : 'bg-blue-50 border border-blue-200'
          }`} 
          data-testid="upload-scope-chip"
          title={isContextualUpload ? 'Scope overrides template project unless project_id is present in the data' : ''}
        >
          <Layers className={`w-4 h-4 ${isContextualUpload ? 'text-purple-600' : 'text-blue-600'}`} />
          <span className={`text-sm font-medium ${isContextualUpload ? 'text-purple-700' : 'text-blue-700'}`}>
            Uploading into:
          </span>
          <span className={`text-sm ${isContextualUpload ? 'text-purple-900' : 'text-blue-900'}`}>
            {scopeDisplay || 'Global (select from template)'}
          </span>
          {isContextualUpload && (
            <Badge variant="outline" className="ml-auto bg-purple-100 text-purple-700 border-purple-300 text-xs">
              Locked
            </Badge>
          )}
        </div>
        
        {/* Contextual scope tooltip */}
        {isContextualUpload && (
          <div className="bg-purple-50 border border-purple-100 rounded p-2 flex items-start gap-2 text-xs text-purple-700">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              Uploads from here are scoped to this project. The template's project column is ignored unless an explicit <code className="bg-purple-100 px-1 rounded">project_id</code> is provided.
            </span>
          </div>
        )}
        
        {/* Version Warning */}
        {versionWarning && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
            <FileWarning className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-amber-800">Legacy Template Detected</p>
              <p className="text-sm text-amber-700 mt-1">{versionWarning}</p>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-2 text-amber-700 border-amber-300 hover:bg-amber-100"
                onClick={async () => {
                  try {
                    // Get signed URL for auth-safe download
                    const response = await api.post('/intake/excel/v3/template/signed-url');
                    const { url } = response.data;
                    const API_URL = process.env.REACT_APP_BACKEND_URL;
                    window.location.href = `${API_URL}${url}`;
                  } catch (error) {
                    // Fallback to authenticated blob download
                    const API_URL = process.env.REACT_APP_BACKEND_URL;
                    const token = localStorage.getItem('auth_token');
                    try {
                      const resp = await fetch(`${API_URL}/api/intake/excel/v3/template.xlsx`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      if (resp.ok) {
                        const blob = await resp.blob();
                        const downloadUrl = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = downloadUrl;
                        a.download = 'ScopeOps_Upload_v3.xlsx';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        window.URL.revokeObjectURL(downloadUrl);
                      }
                    } catch (e) {
                      console.error('Download failed:', e);
                    }
                  }
                }}
              >
                <Download className="w-4 h-4 mr-1" />
                Download v3 Template
              </Button>
            </div>
          </div>
        )}

        {/* SharePoint Status - simplified */}
        {intakeStatus && (
          <div className="flex items-center justify-end text-sm">
            <span className="text-gray-500">
              Template v{intakeStatus.template_version || '3'} • Max {intakeStatus.max_file_size_mb}MB
            </span>
          </div>
        )}

        {/* Upload Mode Tabs */}
        <Tabs value={uploadMode} onValueChange={setUploadMode}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="local" className="flex items-center gap-2" data-testid="upload-tab-local">
              <Upload className="w-4 h-4" />
              Local Files
            </TabsTrigger>
            <TabsTrigger value="sharepoint" className="flex items-center gap-2" data-testid="upload-tab-sharepoint">
              <Cloud className="w-4 h-4" />
              SharePoint / OneDrive
            </TabsTrigger>
          </TabsList>

          {/* Local Upload */}
          <TabsContent value="local" className="mt-4">
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-blue-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              data-testid="upload-dropzone"
            >
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload-modal"
              />
              <label htmlFor="file-upload-modal" className="cursor-pointer">
                <Upload className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                <p className="font-medium text-gray-700">Drop files here or click to browse</p>
                <p className="text-sm text-gray-500 mt-1">Supports .xlsx, .xls, .csv</p>
              </label>
            </div>

            {/* Selected Files List */}
            {files.length > 0 && (
              <div className="mt-4 space-y-2">
                <Label className="text-sm font-medium">Selected Files ({files.length})</Label>
                {files.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="w-4 h-4 text-green-600" />
                      <span className="text-sm truncate max-w-xs">{file.name}</span>
                      <span className="text-xs text-gray-400">
                        ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                    <button 
                      onClick={() => removeFile(idx)} 
                      className="text-gray-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* SharePoint Upload */}
          <TabsContent value="sharepoint" className="mt-4 space-y-4">
            <div>
              <Label htmlFor="sharepoint-url">SharePoint / OneDrive Link</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="sharepoint-url"
                  type="url"
                  placeholder="https://yourcompany.sharepoint.com/..."
                  value={sharepointUrl}
                  onChange={(e) => setSharepointUrl(e.target.value)}
                  className="flex-1"
                  data-testid="sharepoint-url-input"
                />
                <Button variant="outline" size="icon">
                  <Link2 className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Paste a direct link to your Excel file in SharePoint or OneDrive
              </p>
            </div>
            
          </TabsContent>
        </Tabs>

        {/* Upload Options */}
        <div className="flex items-center gap-6 py-2 border-t border-b">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isDryRun}
              onChange={(e) => setIsDryRun(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span className="text-sm">Preview only (dry run)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isUpsert}
              onChange={(e) => setIsUpsert(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span className="text-sm">Update existing (upsert)</span>
          </label>
        </div>

        {/* Progress */}
        {uploading && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Uploading...</span>
              <span className="font-medium">{uploadProgress}%</span>
            </div>
            <Progress value={uploadProgress} className="h-2" />
          </div>
        )}

        {/* Results */}
        {results && (
          <div className={`rounded-lg p-4 ${
            results.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-start gap-3">
              {results.success ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600" />
              )}
              <div className="flex-1">
                <h4 className={`font-medium ${results.success ? 'text-green-900' : 'text-red-900'}`}>
                  {isDryRun ? 'Preview Complete' : results.success ? 'Import Successful' : 'Import Failed'}
                </h4>
                
                {/* Validation Summary */}
                {results.validation && (
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="bg-white rounded px-3 py-2 text-center">
                      <div className="text-lg font-bold text-green-600">{results.validation.rows_create}</div>
                      <div className="text-xs text-gray-500">Create</div>
                    </div>
                    <div className="bg-white rounded px-3 py-2 text-center">
                      <div className="text-lg font-bold text-blue-600">{results.validation.rows_update}</div>
                      <div className="text-xs text-gray-500">Update</div>
                    </div>
                    <div className="bg-white rounded px-3 py-2 text-center">
                      <div className="text-lg font-bold text-gray-600">{results.validation.rows_skip}</div>
                      <div className="text-xs text-gray-500">Skip</div>
                    </div>
                    <div className="bg-white rounded px-3 py-2 text-center">
                      <div className="text-lg font-bold text-red-600">{results.validation.rows_error}</div>
                      <div className="text-xs text-gray-500">Error</div>
                    </div>
                  </div>
                )}

                {/* Template Version Warning */}
                {results.validation?.template_version_mismatch && (
                  <div className="mt-3 bg-amber-100 rounded p-2 flex items-center gap-2">
                    <FileWarning className="w-4 h-4 text-amber-600" />
                    <span className="text-sm text-amber-800">
                      Template v{results.validation.template_version} detected. 
                      Please upgrade to v3 for best results.
                    </span>
                  </div>
                )}

                {/* Error CSV Download */}
                {results.error_csv_available && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadErrors(results.job_id)}
                    className="mt-3"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download Error CSV
                  </Button>
                )}

                {/* Processed Count */}
                {!isDryRun && results.processed > 0 && (
                  <p className="text-sm text-green-700 mt-2">
                    Successfully imported {results.processed} records.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {results ? (
            <>
              <Button variant="outline" onClick={handleReset}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Start Over
              </Button>
              {isDryRun && results.success && results.validation?.rows_create > 0 && (
                <Button onClick={handleProceed} className="bg-green-600 hover:bg-green-700">
                  <Check className="w-4 h-4 mr-1" />
                  Proceed with Import
                </Button>
              )}
              {!isDryRun && results.success && (
                <Button onClick={onClose}>Done</Button>
              )}
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button 
                onClick={handleUpload}
                disabled={uploading || (uploadMode === 'local' && files.length === 0) || (uploadMode === 'sharepoint' && !sharepointUrl)}
                data-testid="start-upload-btn"
              >
                {uploading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-1" />
                    {isDryRun ? 'Preview' : 'Upload'}
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExcelUploadModal;
